import { useEffect, useRef, useState } from 'react';
import { History, RotateCcw, X } from 'lucide-react';
import { useScheduleVersions, useRestoreVersion } from '@/hooks/useCronJobs';
import { useToast } from '@/components/ui/toast';
import { formatRelative } from '@/lib/utils';
import type { ScheduleVersion } from '@/lib/hostApi';

const OP_LABEL: Record<ScheduleVersion['changes'][number]['operation'], string> = {
  INSERT: 'override',
  UPDATE: 'retime',
  DELETE: 'reset',
};

/**
 * Side drawer listing schedule versions (txid groups from
 * harvest.cron_schedule_change_log), newest first, with a per-job diff and a
 * Restore action. Restore routes through the Host commit path (persist + live
 * reschedule) and creates a new version — history is append-only.
 */
export function ScheduleHistoryDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: versions = [], isLoading, isError, error } = useScheduleVersions(50);
  const restore = useRestoreVersion();
  const toast = useToast();
  const [confirming, setConfirming] = useState<number | null>(null);
  const panelRef = useRef<HTMLElement>(null);

  // Move focus into the drawer on open and close on Escape (ARIA dialog pattern).
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function onRestore(txid: number) {
    try {
      const result = await restore.mutateAsync(txid);
      toast.push('success', `Restored version ${txid}; applied ${result.applied.length} change(s) live.`);
    } catch (e) {
      toast.push('error', `Restore failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setConfirming(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <aside
        ref={panelRef}
        tabIndex={-1}
        className="flex h-full w-[480px] max-w-[90vw] flex-col border-l bg-card shadow-2xl outline-none"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Schedule history"
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <History className="h-4 w-4 text-primary" /> Schedule history
          </h2>
          <button onClick={onClose} aria-label="Close history" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isError ? (
            <p className="text-sm text-destructive">Failed to load history: {error instanceof Error ? error.message : String(error)}</p>
          ) : isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : versions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No schedule changes recorded yet.</p>
          ) : (
            <ul className="space-y-3">
              {versions.map((v) => (
                <li key={v.txid} className="rounded-lg border border-border bg-background/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      {formatRelative(v.changedAt)} · {v.changes.length} job{v.changes.length === 1 ? '' : 's'}
                    </span>
                    <button
                      className="inline-flex items-center gap-1 rounded bg-primary/15 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/25"
                      onClick={() => setConfirming(v.txid)}
                      disabled={restore.isPending}
                    >
                      <RotateCcw className="h-3 w-3" /> Restore
                    </button>
                  </div>
                  {v.reason && <p className="mt-1 truncate text-xs text-muted-foreground" title={v.reason}>{v.reason}</p>}
                  <ul className="mt-2 space-y-1">
                    {v.changes.map((c) => (
                      <li key={`${v.txid}-${c.jobId}`} className="font-mono text-[11px] leading-relaxed text-foreground/80">
                        <span className="mr-1 uppercase text-muted-foreground">{OP_LABEL[c.operation]}</span>
                        {c.jobId}
                        {': '}
                        <span className="text-muted-foreground">{c.oldCron ?? '—'}</span>
                        {' → '}
                        <span>{c.newCron ?? '(reset)'}</span>
                      </li>
                    ))}
                  </ul>
                  {confirming === v.txid && (
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
                        disabled={restore.isPending}
                        onClick={() => onRestore(v.txid)}
                      >
                        {restore.isPending ? 'Restoring…' : 'Confirm restore'}
                      </button>
                      <button
                        className="rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setConfirming(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
